import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import CustomInput from "./CustomInput";
import CustomDropdown from "./CustomDropdown";
import AgreementDetailsModal from "./AgreementDetailsModal";
import ProfileInfoController from "../controller/profileInfoController";
import { clearHomePageInfosClientCache, getHomePageInfos } from "../utils/apiClient";
import AppConstants from "../utils/appConstants";
import dropdownOptions from "../data/dropdownOptions.json";
import {
  getSubcategoriesForIndustry,
  subcategoryRequiresProfession,
  getSubcategoryLabel,
} from "../data/employmentSubcategories";
import countries from "world-countries";
import "../pages/css/ProfileInformation.css";
import { undoSignatureStroke } from "../utils/signatureCanvas";

const STEPS = [
  "Account Info",
  "Personal Info",
  "Address Info",
  "Regulations",
  "Documents",
  "Agreements",
];

const {
  clientTypeOptions,
  internalTypeOptions,
  profileGenderOptions,
  profileMaritalOptions,
  residenceOptions,
  employmentStatusOptions,
  employmentIndustryOptions,
  educationLevelOptions,
  sourceOfWealthOptions,
  netWorthOptions,
  annualIncomeOptions,
  annualDepositOptions,
  experienceOptions,
  identityDocTypeOptions,
  addressDocTypeOptions,
  tradingStrategyOptions,
  monthlyDepositOptions,
  fundingMethodOptions,
  tradingOnBehalfOfOptions,
} = dropdownOptions;

const ONE_FACE_DOC_TYPES = [2, 5];

const ADDRESS_PARTS = [
  ["neighborhood", "Neighborhood"],
  ["street", "Street"],
  ["buildingName", "Building Name"],
  ["besideTo", "Beside To"],
  ["floor", "Floor"],
  ["block", "Block"],
  ["buildingNumber", "Building Number"],
  ["streetNumber", "Street Number"],
  ["landlinePhone", "Landline Phone"],
  ["boxNumber", "Box Number"],
];

function parseStructuredAddress(raw) {
  if (!raw || typeof raw !== "string") return null;
  const found = {};
  let hits = 0;
  raw.split(/\r?\n/).forEach((line) => {
    const splitAt = line.indexOf(": ");
    if (splitAt === -1) return;
    const label = line.slice(0, splitAt).trim().toLowerCase();
    const value = line.slice(splitAt + 2).trim();
    const match = ADDRESS_PARTS.find(([, partLabel]) => partLabel.toLowerCase() === label);
    if (match && value) {
      found[match[0]] = value;
      hits += 1;
    }
  });
  return hits > 0 ? found : null;
}

function findOption(options, value) {
  if (value == null || Array.isArray(value)) return null;
  return options.find(
    (o) => o.value === value || o.value === Number(value) || String(o.value) === String(value)
  ) || null;
}

function hasTextValue(v) {
  return typeof v === "string" && v.trim().length > 0;
}

function hasDropdownValue(v) {
  return v != null;
}

function hasArrayValue(v) {
  return Array.isArray(v) && v.length > 0;
}

function hasNumberValue(v) {
  return v !== null && v !== undefined;
}

export default function ProfileCompletionWizard({ user: externalUser, onComplete }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [user, setUser] = useState(externalUser || null);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const lockedRef = useRef(new Set());
  const rawInfoRef = useRef({});

  const [clientType, setClientType] = useState(null);
  const [accountType, setAccountType] = useState(null);
  const [nickName, setNickName] = useState("");

  const [firstName, setFirstName] = useState("");
  const [fatherName, setFatherName] = useState("");
  const [lastName, setLastName] = useState("");
  const [motherName, setMotherName] = useState("");
  const [gender, setGender] = useState(null);
  const [profession, setProfession] = useState("");
  const [maritalStatus, setMaritalStatus] = useState(null);
  const [spouseName, setSpouseName] = useState("");
  const [spouseProfession, setSpouseProfession] = useState("");
  const [isPep, setIsPep] = useState(false);
  const [birthDate, setBirthDate] = useState("");
  const [placeOfBirth, setPlaceOfBirth] = useState("");

  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [street, setStreet] = useState("");
  const [buildingName, setBuildingName] = useState("");
  const [besideTo, setBesideTo] = useState("");
  const [floor, setFloor] = useState("");
  const [block, setBlock] = useState("");
  const [buildingNumber, setBuildingNumber] = useState("");
  const [streetNumber, setStreetNumber] = useState("");
  const [landlinePhone, setLandlinePhone] = useState("");
  const [boxNumber, setBoxNumber] = useState("");
  const [workAddress, setWorkAddress] = useState("");
  const [city, setCity] = useState("");
  const [residence, setResidence] = useState(null);
  const [country, setCountry] = useState("");
  const [nationality, setNationality] = useState("");

  const [taxId, setTaxId] = useState("");
  const [netWorth, setNetWorth] = useState(null);
  const [annualIncome, setAnnualIncome] = useState(null);
  const [annualDeposit, setAnnualDeposit] = useState(null);
  const [tradingStrategy, setTradingStrategy] = useState(null);
  const [tradingStrategyOther, setTradingStrategyOther] = useState("");
  const [monthlyDeposit, setMonthlyDeposit] = useState(null);
  const [fundingMethod, setFundingMethod] = useState(null);
  const [fundingMethodOther, setFundingMethodOther] = useState("");
  const [depositsFromOwnName, setDepositsFromOwnName] = useState(null);
  const [tradingForYourself, setTradingForYourself] = useState(null);
  const [tradingOnBehalfOf, setTradingOnBehalfOf] = useState(null);
  const [tradingOnBehalfOther, setTradingOnBehalfOther] = useState("");
  const [employmentIndustry, setEmploymentIndustry] = useState(null);
  const [employmentSubcategory, setEmploymentSubcategory] = useState(null);
  const [educationLevel, setEducationLevel] = useState(null);
  const [sourceOfWealth, setSourceOfWealth] = useState(null);
  const [employmentStatus, setEmploymentStatus] = useState(null);
  const [experienceForex, setExperienceForex] = useState(null);
  const [experienceStocks, setExperienceStocks] = useState(null);
  const [experienceFutures, setExperienceFutures] = useState(null);
  const [experienceCfd, setExperienceCfd] = useState(null);

  const [identityDocType, setIdentityDocType] = useState(null);
  const [addressDocType, setAddressDocType] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [frontIdFile, setFrontIdFile] = useState(null);
  const [backIdFile, setBackIdFile] = useState(null);
  const [addressFile, setAddressFile] = useState(null);
  const [addressEntryMode, setAddressEntryMode] = useState("details");
  const [signatureVideoFile, setSignatureVideoFile] = useState(null);

  const [agreements, setAgreements] = useState([]);
  const [agreementChecks, setAgreementChecks] = useState({});
  const [agreementSigned, setAgreementSigned] = useState({});
  const [allAgreementsChecked, setAllAgreementsChecked] = useState(false);
  const [loadingAgreements, setLoadingAgreements] = useState(false);
  const [agreementDetailsModal, setAgreementDetailsModal] = useState({
    open: false,
    key: "",
    name: "",
  });
  const sigCanvasRefs = useRef({});

  const resolvedUserId = useMemo(() => {
    return (
      profileData?.id ??
      profileData?.userId ??
      profileData?.user_id ??
      user?.id ??
      user?.ID ??
      user?.user_id ??
      user?.userId ??
      null
    );
  }, [profileData, user]);

  const isLocked = (key) => lockedRef.current.has(key);

  const showTradingStrategyOther = tradingStrategy?.value === 4;
  const showFundingMethodOther = fundingMethod?.value === 6;
  const showTradingOnBehalfOf = tradingForYourself === 0;
  const showTradingOnBehalfOther = showTradingOnBehalfOf && tradingOnBehalfOf?.value === 4;

  const countryOptions = useMemo(() => {
    return countries
      .filter((c) => c.cca2 !== "IL")
      .map((c) => ({ value: c.cca2, label: `${c.name.common} (${c.cca2})` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  useEffect(() => {
    if (externalUser) setUser(externalUser);
  }, [externalUser]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const populateFields = useCallback((data) => {
    const locked = new Set();

    const lockText = (key, val) => { if (hasTextValue(val)) locked.add(key); };
    const lockDrop = (key, val) => { if (hasDropdownValue(val)) locked.add(key); };

    const rawInfo = data.raw?.user_information || data.raw?.USER_INFORMATION || {};
    rawInfoRef.current = rawInfo;

    const ct = findOption(clientTypeOptions, data.type);
    const at = findOption(internalTypeOptions, data.internalType);
    setClientType(ct);     lockDrop("clientType", ct);
    setAccountType(at);    lockDrop("accountType", at);
    setNickName(data.nickName || "");                lockText("nickName", data.nickName);

    setFirstName(data.firstName || "");              lockText("firstName", data.firstName);
    setFatherName(data.fatherName || "");            lockText("fatherName", data.fatherName);
    setLastName(data.lastName || "");                lockText("lastName", data.lastName);
    setMotherName(data.motherName || "");            lockText("motherName", data.motherName);
    const g = findOption(profileGenderOptions, data.gender);
    setGender(g);                                    lockDrop("gender", g);
    setProfession(data.profession || "");            lockText("profession", data.profession);
    const m = findOption(profileMaritalOptions, data.marital);
    setMaritalStatus(m);                             lockDrop("maritalStatus", m);
    setSpouseName(data.spouseName || "");            lockText("spouseName", data.spouseName);
    setSpouseProfession(data.spouseProfession || ""); lockText("spouseProfession", data.spouseProfession);
    setIsPep(data.isPep === true || data.isPep === 1);
    if (data.isPep === true || data.isPep === 1) locked.add("isPep");
    const bd = data.birthDay ? data.birthDay.split("T")[0] : "";
    setBirthDate(bd);                                lockText("birthDate", bd);
    setPlaceOfBirth(data.placeOfBirth || "");        lockText("placeOfBirth", data.placeOfBirth);

    setState(data.state || "");                      lockText("state", data.state);
    setZipCode(data.zipCode || "");                  lockText("zipCode", data.zipCode);
    const addressParts = parseStructuredAddress(data.address);
    const partValues = {
      neighborhood: addressParts?.neighborhood || "",
      street: addressParts?.street || "",
      buildingName: addressParts?.buildingName || "",
      besideTo: addressParts?.besideTo || "",
      floor: addressParts?.floor || "",
      block: addressParts?.block || "",
      buildingNumber: addressParts?.buildingNumber || "",
      streetNumber: addressParts?.streetNumber || "",
      landlinePhone: addressParts?.landlinePhone || "",
      boxNumber: addressParts?.boxNumber || "",
    };
    setNeighborhood(partValues.neighborhood);        lockText("neighborhood", partValues.neighborhood);
    setStreet(partValues.street);                    lockText("street", partValues.street);
    setBuildingName(partValues.buildingName);        lockText("buildingName", partValues.buildingName);
    setBesideTo(partValues.besideTo);                lockText("besideTo", partValues.besideTo);
    setFloor(partValues.floor);                      lockText("floor", partValues.floor);
    setBlock(partValues.block);                      lockText("block", partValues.block);
    setBuildingNumber(partValues.buildingNumber);    lockText("buildingNumber", partValues.buildingNumber);
    setStreetNumber(partValues.streetNumber);        lockText("streetNumber", partValues.streetNumber);
    setLandlinePhone(partValues.landlinePhone);      lockText("landlinePhone", partValues.landlinePhone);
    setBoxNumber(partValues.boxNumber);              lockText("boxNumber", partValues.boxNumber);
    setWorkAddress(data.workAddress || "");           lockText("workAddress", data.workAddress);
    setCity(data.city || "");                        lockText("city", data.city);
    const r = findOption(residenceOptions, data.residence);
    setResidence(r);                                 lockDrop("residence", r);
    setCountry(data.country || "");                  lockText("country", data.country);
    setNationality(data.nationality || "");          lockText("nationality", data.nationality);

    setTaxId(data.taxId || "");                      lockText("taxId", data.taxId);
    const nw = findOption(netWorthOptions, data.netWorth);
    setNetWorth(nw);                                 lockDrop("netWorth", nw);
    const ai = findOption(annualIncomeOptions, data.annualIncome);
    setAnnualIncome(ai);                             lockDrop("annualIncome", ai);
    const ad = findOption(annualDepositOptions, data.annualDeposit);
    setAnnualDeposit(ad);                            lockDrop("annualDeposit", ad);
    const ts = findOption(tradingStrategyOptions, data.tradingStrategy);
    setTradingStrategy(ts);                          lockDrop("tradingStrategy", ts);
    setTradingStrategyOther(data.tradingStrategyOther || ""); lockText("tradingStrategyOther", data.tradingStrategyOther);
    const md = findOption(monthlyDepositOptions, data.monthlyDeposit);
    setMonthlyDeposit(md);                           lockDrop("monthlyDeposit", md);
    const firstFunding = Array.isArray(data.fundingMethods)
      ? (data.fundingMethods.length > 0 ? data.fundingMethods[0] : null)
      : data.fundingMethods;
    const fm = findOption(fundingMethodOptions, firstFunding);
    setFundingMethod(fm);                            lockDrop("fundingMethods", fm);
    setFundingMethodOther(data.fundingMethodOther || ""); lockText("fundingMethodOther", data.fundingMethodOther);
    const dfon = data.depositsFromOwnName != null ? Number(data.depositsFromOwnName) : null;
    setDepositsFromOwnName(dfon);                    if (hasNumberValue(dfon)) locked.add("depositsFromOwnName");
    const tfy = data.tradingForYourself != null ? Number(data.tradingForYourself) : null;
    setTradingForYourself(tfy);                      if (hasNumberValue(tfy)) locked.add("tradingForYourself");
    const tob = findOption(tradingOnBehalfOfOptions, data.tradingOnBehalfOf);
    setTradingOnBehalfOf(tob);                       lockDrop("tradingOnBehalfOf", tob);
    setTradingOnBehalfOther(data.tradingOnBehalfOther || ""); lockText("tradingOnBehalfOther", data.tradingOnBehalfOther);
    const ei = findOption(employmentIndustryOptions, data.employmentIndustry);
    setEmploymentIndustry(ei);                       lockDrop("employmentIndustry", ei);
    const es = findOption(employmentStatusOptions, data.employmentStatus);
    setEmploymentStatus(es);                         lockDrop("employmentStatus", es);
    const subOpts = getSubcategoriesForIndustry(data.employmentIndustry, data.employmentStatus);
    const esc = findOption(subOpts, data.employmentSubcategory);
    setEmploymentSubcategory(esc);                   lockDrop("employmentSubcategory", esc);
    const el = findOption(educationLevelOptions, data.educationLevel);
    setEducationLevel(el);                           lockDrop("educationLevel", el);
    const sw = findOption(sourceOfWealthOptions, data.sourceOfWealth);
    setSourceOfWealth(sw);                           lockDrop("sourceOfWealth", sw);
    const ef = findOption(experienceOptions, data.experienceForex);
    setExperienceForex(ef);                          lockDrop("experienceForex", ef);
    const est = findOption(experienceOptions, data.experienceStocks);
    setExperienceStocks(est);                        lockDrop("experienceStocks", est);
    const efu = findOption(experienceOptions, data.experienceFutures);
    setExperienceFutures(efu);                       lockDrop("experienceFutures", efu);
    const ec = findOption(experienceOptions, data.experienceCfd);
    setExperienceCfd(ec);                            lockDrop("experienceCfd", ec);

    lockedRef.current = locked;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!externalUser) {
          let userDataFromStorage = null;
          try {
            const storedUserData = sessionStorage.getItem("userData");
            const storedTimestamp = sessionStorage.getItem("userDataTimestamp");
            if (storedUserData && storedTimestamp) {
              const age = Date.now() - parseInt(storedTimestamp, 10);
              if (age < 5 * 60 * 1000) {
                userDataFromStorage = JSON.parse(storedUserData);
                setUser(userDataFromStorage);
              }
            }
          } catch (_) {}

          if (!userDataFromStorage) {
            const userResponse = await getHomePageInfos();
            const userData = userResponse?.decryptedData || userResponse?.data;
            if (userData && userData.CODE === AppConstants.responseOk && userData.DATA) {
              setUser(userData.DATA);
              try {
                sessionStorage.setItem("userData", JSON.stringify(userData.DATA));
                sessionStorage.setItem("userDataTimestamp", Date.now().toString());
              } catch (_) {}
            }
          }
        }

        const [result, agreementResult] = await Promise.all([
          ProfileInfoController.getProfileInfo(),
          ProfileInfoController.getAgreements(),
        ]);
        if (result.success) {
          setProfileData(result.data);
          populateFields(result.data);
        } else {
          setError(result.error);
        }
        if (agreementResult.success && agreementResult.data) {
          setAgreements(agreementResult.data);
        }
      } catch (err) {
        setError(err.message || "Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [populateFields, externalUser]);

  const clearFieldError = (field) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const selectAddressMode = (mode) => {
    setAddressEntryMode(mode);
    setFieldErrors({});
  };

  const scrollToField = (fieldKey) => {
    if (!fieldKey) return;
    requestAnimationFrame(() => {
      const target = document.querySelector(`[data-field-key="${fieldKey}"]`);
      if (target && typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  };

  const validateStep1 = () => {
    const errs = {};
    if (!isLocked("clientType") && !clientType) errs.clientType = true;
    if (!isLocked("accountType") && !accountType) errs.accountType = true;
    if (!isLocked("nickName") && (!nickName.trim() || nickName.trim().length < 3)) errs.nickName = true;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    const errs = {};
    if (!isLocked("firstName") && (!firstName.trim() || firstName.trim().length < 2)) errs.firstName = true;
    if (!isLocked("fatherName") && (!fatherName.trim() || fatherName.trim().length < 2)) errs.fatherName = true;
    if (!isLocked("lastName") && (!lastName.trim() || lastName.trim().length < 2)) errs.lastName = true;
    if (!isLocked("motherName") && (!motherName.trim() || motherName.trim().length < 2)) errs.motherName = true;
    if (!isLocked("gender") && !gender) errs.gender = true;
    if (!isLocked("maritalStatus") && !maritalStatus) errs.maritalStatus = true;
    if (maritalStatus?.value === 1) {
      if (!isLocked("spouseName") && (!spouseName.trim() || spouseName.trim().length < 2)) errs.spouseName = true;
      if (!isLocked("spouseProfession") && (!spouseProfession.trim() || spouseProfession.trim().length < 2)) errs.spouseProfession = true;
    }
    if (!isLocked("birthDate") && !birthDate) errs.birthDate = true;
    if (!isLocked("placeOfBirth") && (!placeOfBirth.trim() || placeOfBirth.trim().length < 2)) errs.placeOfBirth = true;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep3 = () => {
    const errs = {};
    if (addressEntryMode === "proof") {
      // Proof of address replaces the address form. Document type and file are enough.
      if (!addressDocType) errs.addressDocType = true;
      if (!addressFile) errs.addressFile = true;
    } else {
      if (!isLocked("state") && (!state.trim() || state.trim().length < 2)) errs.state = true;
      if (!isLocked("zipCode") && !zipCode.trim()) errs.zipCode = true;
      if (!isLocked("city") && (!city.trim() || city.trim().length < 2)) errs.city = true;
      if (!isLocked("neighborhood") && neighborhood.trim().length < 2) errs.neighborhood = true;
      if (!isLocked("street") && street.trim().length < 2) errs.street = true;
      if (!isLocked("buildingName") && buildingName.trim().length < 2) errs.buildingName = true;
      if (!isLocked("besideTo") && besideTo.trim().length < 2) errs.besideTo = true;
      if (!isLocked("floor") && !floor.trim()) errs.floor = true;
      if (!isLocked("landlinePhone") && !landlinePhone.trim()) errs.landlinePhone = true;
      if (!isLocked("workAddress") && !workAddress.trim()) errs.workAddress = true;
      if (!isLocked("residence") && residence == null) errs.residence = true;
      if (!isLocked("country") && !country.trim()) errs.country = true;
      if (!isLocked("nationality") && !nationality.trim()) errs.nationality = true;
    }
    setFieldErrors(errs);
    const firstMissingField = Object.keys(errs)[0];
    if (firstMissingField) {
      scrollToField(firstMissingField);
    }
    return Object.keys(errs).length === 0;
  };

  const validateStep4 = () => {
    const errs = {};
    if (!isLocked("taxId") && !taxId.trim()) errs.taxId = true;
    if (!isLocked("netWorth") && !netWorth) errs.netWorth = true;
    if (!isLocked("annualIncome") && !annualIncome) errs.annualIncome = true;
    if (!isLocked("annualDeposit") && !annualDeposit) errs.annualDeposit = true;
    if (!isLocked("tradingStrategy") && !tradingStrategy) errs.tradingStrategy = true;
    if (!isLocked("tradingStrategyOther") && showTradingStrategyOther && !tradingStrategyOther.trim()) {
      errs.tradingStrategyOther = true;
    }
    if (!isLocked("monthlyDeposit") && !monthlyDeposit) errs.monthlyDeposit = true;
    if (!isLocked("fundingMethods") && !fundingMethod) {
      errs.fundingMethods = true;
    }
    if (!isLocked("fundingMethodOther") && showFundingMethodOther && !fundingMethodOther.trim()) {
      errs.fundingMethodOther = true;
    }
    if (!isLocked("depositsFromOwnName") && (depositsFromOwnName === null || depositsFromOwnName === undefined)) {
      errs.depositsFromOwnName = true;
    }
    if (!isLocked("tradingForYourself") && (tradingForYourself === null || tradingForYourself === undefined)) {
      errs.tradingForYourself = true;
    }
    if (!isLocked("tradingOnBehalfOf") && showTradingOnBehalfOf && !tradingOnBehalfOf) {
      errs.tradingOnBehalfOf = true;
    }
    if (!isLocked("tradingOnBehalfOther") && showTradingOnBehalfOther && !tradingOnBehalfOther.trim()) {
      errs.tradingOnBehalfOther = true;
    }
    if (!isLocked("employmentIndustry") && !employmentIndustry) errs.employmentIndustry = true;
    if (!isLocked("employmentSubcategory") && !employmentSubcategory) errs.employmentSubcategory = true;
    if (
      !isLocked("profession") &&
      subcategoryRequiresProfession(employmentSubcategory?.value) &&
      (!profession.trim() || profession.trim().length < 2)
    ) {
      errs.profession = true;
    }
    if (!isLocked("educationLevel") && !educationLevel) errs.educationLevel = true;
    if (!isLocked("sourceOfWealth") && !sourceOfWealth) errs.sourceOfWealth = true;
    if (!isLocked("employmentStatus") && !employmentStatus) errs.employmentStatus = true;
    if (!isLocked("experienceForex") && !experienceForex) errs.experienceForex = true;
    if (!isLocked("experienceStocks") && !experienceStocks) errs.experienceStocks = true;
    if (!isLocked("experienceFutures") && !experienceFutures) errs.experienceFutures = true;
    if (!isLocked("experienceCfd") && !experienceCfd) errs.experienceCfd = true;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const resolveProfessionValue = () => {
    const raw = rawInfoRef.current || {};
    if (subcategoryRequiresProfession(employmentSubcategory?.value)) {
      return profession.trim() || raw.PROFESSION || "";
    }
    if (employmentSubcategory?.value != null) {
      return (
        getSubcategoryLabel(employmentSubcategory.value) ||
        employmentSubcategory.label ||
        profession.trim() ||
        raw.PROFESSION ||
        ""
      );
    }
    return profession.trim() || raw.PROFESSION || "";
  };

  const composeStructuredAddress = () => {
    const values = {
      neighborhood,
      street,
      buildingName,
      besideTo,
      floor,
      block,
      buildingNumber,
      streetNumber,
      landlinePhone,
      boxNumber,
    };
    return ADDRESS_PARTS
      .map(([key, label]) => {
        const value = (values[key] || "").trim();
        return value ? `${label}: ${value}` : "";
      })
      .filter(Boolean)
      .join("\n");
  };

  const buildFullUserInformation = () => {
    const raw = rawInfoRef.current;
    const resolvedProfession = resolveProfessionValue();
    return {
      USER_ID: profileData?.id ?? raw.USER_ID,
      F_NAME: firstName.trim() || raw.F_NAME || "",
      L_NAME: lastName.trim() || raw.L_NAME || "",
      FATHER_NAME: fatherName.trim() || raw.FATHER_NAME || "",
      MOTHER_NAME: motherName.trim() || raw.MOTHER_NAME || "",
      PLACE_OF_BIRTH: placeOfBirth.trim() || raw.PLACE_OF_BIRTH || "",
      // Never wipe an existing profession with empty during early steps
      PROFESSION: resolvedProfession || raw.PROFESSION || "",
      SPOUSE_NAME: spouseName.trim() || raw.SPOUSE_NAME || null,
      SPOUSE_PROFESSION: spouseProfession.trim() || raw.SPOUSE_PROFESSION || null,
      BIRTH_DAY: birthDate || raw.BIRTH_DAY || null,
      NATIONALITY: nationality.trim() || raw.NATIONALITY || "",
      GENDER: gender?.value ?? raw.GENDER ?? 0,
      MARITAL: maritalStatus?.value ?? raw.MARITAL ?? null,
      BENEFICAL_RIGHT: raw.BENEFICAL_RIGHT ?? null,
      ADDRESS: composeStructuredAddress() || raw.ADDRESS || "",
      WORK_ADDRESS: workAddress.trim() || raw.WORK_ADDRESS || "",
      RESIDENCE: residence?.value ?? raw.RESIDENCE ?? null,
      COUNTRY: country.trim() || raw.COUNTRY || "",
      CITY: city.trim() || raw.CITY || "",
      STATE: state.trim() || raw.STATE || "",
      ZIP_CODE: zipCode.trim() || raw.ZIP_CODE || "",
      PHONE: raw.PHONE || "",
      EMAIL: raw.EMAIL || "",
      IDENTIFICATION_NUMBER: raw.IDENTIFICATION_NUMBER || "",
      RISK_NOTE: raw.RISK_NOTE ?? null,
      RISK_STATUS: raw.RISK_STATUS ?? 0,
      IS_PEP: isPep ? 1 : 0,
      INTERNAL_TYPE: raw.INTERNAL_TYPE ?? null,
      DEFAULT_LEVERAGE: raw.DEFAULT_LEVERAGE ?? null,
      REFERAL_BROKER: raw.REFERAL_BROKER ?? null,
      REGISTER_NUMBER: raw.REGISTER_NUMBER || "",
      REGISTER_PLACE: raw.REGISTER_PLACE || "",
      OTHER_NATIONALITY: raw.OTHER_NATIONALITY || "",
    };
  };

  const buildUserRegulation = () => {
    return {
      USER_ID: resolvedUserId,
      TAX_ID: taxId.trim() || null,
      EMPLOYMENT_STATUS: employmentStatus?.value ?? null,
      EMPLOYMENT_INDUSTRY: employmentIndustry?.value ?? null,
      EMPLOYMENT_SUBCATEGORY: employmentSubcategory?.value ?? null,
      EDUCATION_LEVEL: educationLevel?.value ?? null,
      SOURCE_OF_WEALTH: sourceOfWealth?.value ?? null,
      NET_WORTH: netWorth?.value ?? null,
      ANNUAL_INCOME: annualIncome?.value ?? null,
      ANNUAL_DEPOSIT: annualDeposit?.value ?? null,
      TRADING_STRATEGY: tradingStrategy?.value ?? null,
      TRADING_STRATEGY_OTHER: showTradingStrategyOther ? (tradingStrategyOther.trim() || null) : null,
      MONTHLY_DEPOSIT: monthlyDeposit?.value ?? null,
      FUNDING_METHODS: fundingMethod?.value != null ? [fundingMethod.value] : [],
      FUNDING_METHOD_OTHER: showFundingMethodOther ? (fundingMethodOther.trim() || null) : null,
      DEPOSITS_FROM_OWN_NAME: depositsFromOwnName,
      TRADING_FOR_YOURSELF: tradingForYourself,
      TRADING_ON_BEHALF_OF: showTradingOnBehalfOf ? (tradingOnBehalfOf?.value ?? null) : null,
      TRADING_ON_BEHALF_OTHER: showTradingOnBehalfOther ? (tradingOnBehalfOther.trim() || null) : null,
      EXPERIENCE_FOREX: experienceForex?.value ?? null,
      EXPERIENCE_STOCKS: experienceStocks?.value ?? null,
      EXPERIENCE_FUTURES: experienceFutures?.value ?? null,
      EXPERIENCE_CFD: experienceCfd?.value ?? null,
    };
  };

  const validateStep5 = () => {
    const errs = {};
    if (!selfieFile) errs.selfieFile = true;
    if (!identityDocType) errs.identityDocType = true;
    if (!frontIdFile) errs.frontIdFile = true;
    if (!ONE_FACE_DOC_TYPES.includes(identityDocType?.value) && !backIdFile) errs.backIdFile = true;
    setFieldErrors(errs);
    const firstMissingField = Object.keys(errs)[0];
    if (firstMissingField) {
      scrollToField(firstMissingField);
    }
    return Object.keys(errs).length === 0;
  };

  const isApplicableAgreement = useCallback((ag) => {
    const key = (ag.KEY || ag.NAME || "").toLowerCase();
    return !(key === "ib_agreement" && profileData?.type !== 2);
  }, [profileData?.type]);

  const applicableAgreements = useMemo(
    () => agreements.filter(isApplicableAgreement),
    [agreements, isApplicableAgreement]
  );

  const signedAgreementCount = applicableAgreements.filter((ag) => {
    const id = ag.ID ?? ag.id ?? 0;
    return !!agreementChecks[id] && !!agreementSigned[id];
  }).length;

  const requiredSignatureCount = Math.min(3, applicableAgreements.length);

  const markAgreementSigned = (id) => {
    const canvas = sigCanvasRefs.current[id];
    setAgreementSigned((prev) => ({
      ...prev,
      [id]: !!(canvas && typeof canvas.isEmpty === "function" && !canvas.isEmpty()),
    }));
  };

  const validateStep6 = () => {
    let firstMissingField = null;
    setError(null);

    let signedCount = 0;
    for (const ag of applicableAgreements) {
      const id = ag.ID ?? ag.id ?? 0;

      if (!agreementChecks[id]) {
        if (!firstMissingField) firstMissingField = `agreement-check-${id}`;
        continue;
      }

      const canvas = sigCanvasRefs.current[id];
      if (canvas && !canvas.isEmpty()) signedCount += 1;
    }

    if (firstMissingField) {
      setError("Check every agreement. Sign at least 3 of them.");
      scrollToField(firstMissingField);
      return false;
    }

    if (signedCount < requiredSignatureCount) {
      const firstUnsigned = applicableAgreements.find((ag) => {
        const id = ag.ID ?? ag.id ?? 0;
        const canvas = sigCanvasRefs.current[id];
        return !canvas || canvas.isEmpty();
      });
      const missingId = firstUnsigned?.ID ?? firstUnsigned?.id;
      setError(
        `Sign at least ${requiredSignatureCount} agreements. ${signedCount} out of ${applicableAgreements.length} signed. The rest only need to be checked.`
      );
      if (missingId != null) scrollToField(`agreement-sign-${missingId}`);
      return false;
    }

    return true;
  };

  const buildSignatureFiles = async () => {
    const signatureFiles = {};
    for (const ag of applicableAgreements) {
      const id = ag.ID ?? ag.id ?? 0;
      if (!agreementChecks[id]) continue;
      const canvas = sigCanvasRefs.current[id];
      if (canvas && !canvas.isEmpty()) {
        // Avoid getTrimmedCanvas() — trim-canvas default export breaks under webpack
        const dataUrl =
          typeof canvas.toDataURL === "function"
            ? canvas.toDataURL("image/png")
            : canvas.getCanvas().toDataURL("image/png");
        const resp = await fetch(dataUrl);
        const blob = await resp.blob();
        signatureFiles[id] = new File([blob], `agreement_${id}_signature.png`, { type: "image/png" });
      }
    }
    if (Object.keys(signatureFiles).length < requiredSignatureCount) {
      throw new Error(`Sign at least ${requiredSignatureCount} agreements before saving.`);
    }
    // Admin shows the signature from the client agreement image. Checked
    // agreements that were not drawn still need that image attached.
    const clientAgreement = applicableAgreements.find(
      (ag) => String(ag.KEY || ag.key || "").toLowerCase() === "client_agreement"
    );
    const clientAgreementId = clientAgreement?.ID ?? clientAgreement?.id;
    const firstSignature = Object.values(signatureFiles)[0];
    if (clientAgreementId && firstSignature && !signatureFiles[clientAgreementId]) {
      signatureFiles[clientAgreementId] = firstSignature;
    }
    return signatureFiles;
  };

  const handleNext = async () => {
    let valid = false;
    if (currentStep === 0) valid = validateStep1();
    else if (currentStep === 1) valid = validateStep2();
    else if (currentStep === 2) valid = validateStep3();
    else if (currentStep === 3) valid = validateStep4();
    else if (currentStep === 4) valid = validateStep5();
    else if (currentStep === 5) valid = validateStep6();
    if (!valid) return;

    try {
      setSaving(true);
      setError(null);

      let result;

      if (currentStep === 0) {
        result = await ProfileInfoController.saveProfile({
          userId: profileData?.id,
          nickName: nickName.trim(),
          userType: clientType?.value,
          updateType: "client",
        });
      } else if (currentStep === 1 || currentStep === 2) {
        result = await ProfileInfoController.saveProfile({
          userId: profileData?.id,
          userInformation: buildFullUserInformation(),
          updateType: currentStep === 1 ? "personal" : "address",
        });
      } else if (currentStep === 3) {
        const userInformation = buildFullUserInformation();
        // Persist resolved profession into local raw cache so later steps don't wipe it
        if (userInformation.PROFESSION) {
          rawInfoRef.current = {
            ...(rawInfoRef.current || {}),
            PROFESSION: userInformation.PROFESSION,
          };
          setProfession(userInformation.PROFESSION);
        }
        result = await ProfileInfoController.saveRegulation({
          userId: resolvedUserId,
          userInformation,
          userRegulation: buildUserRegulation(),
        });
      } else if (currentStep === 4) {
        result = { success: true };
      } else if (currentStep === 5) {
        // Re-save profession/subcategory before documents — earlier steps may have left PROFESSION empty
        const userInformation = buildFullUserInformation();
        if (userInformation.PROFESSION || employmentSubcategory?.value != null) {
          rawInfoRef.current = {
            ...(rawInfoRef.current || {}),
            PROFESSION: userInformation.PROFESSION || rawInfoRef.current?.PROFESSION || "",
          };
          await ProfileInfoController.saveRegulation({
            userId: resolvedUserId,
            userInformation,
            userRegulation: buildUserRegulation(),
          });
        }

        const imageFiles = {};
        // Keep multipart field names aligned with mobile/create-documents API contract.
        if (selfieFile) imageFiles.SELFIE_IMAGE = selfieFile;
        if (frontIdFile) imageFiles.FRONT_ID_IMAGE = frontIdFile;
        if (backIdFile) imageFiles.BACK_ID_IMAGE = backIdFile;
        // Address proof is only sent when the user chose upload instead of filling the address form.
        if (addressEntryMode === "proof" && addressFile) imageFiles.ADDRESS_IMAGE = addressFile;
        if (signatureVideoFile) imageFiles.SIGNATURE_VIDEO = signatureVideoFile;

        let agreementSignatures;
        try {
          agreementSignatures = await buildSignatureFiles();
        } catch (signatureError) {
          setError(signatureError?.message || "Sign at least 3 agreements before saving.");
          return;
        }

        result = await ProfileInfoController.submitDocuments({
          email: profileData?.userName || profileData?.email,
          identityDocType: identityDocType?.value,
          addressDocType: addressEntryMode === "proof" ? addressDocType?.value : null,
          imageFiles,
          agreementSignatures,
        });
      }

      if (
        !result?.success &&
        currentStep === STEPS.length - 1 &&
        Number(result?.code) === AppConstants.responseFailed
      ) {
        const recovery = await ProfileInfoController.completeProfile();
        if (recovery?.success) {
          result = { success: true };
        }
      }

      if (result?.success) {
        if (currentStep < STEPS.length - 1) {
          setCurrentStep((s) => s + 1);
          setFieldErrors({});
        } else {
          const finalize = await ProfileInfoController.completeProfile();
          // Risk-score enum noise after a successful save should not block finish.
          const finalizeNoise =
            /RESTRICTED_COUNTRIES|Undefined constant.*BusinessKey|description\(\) on null/i.test(
              String(finalize?.error || "")
            );
          if (!finalize?.success && !finalizeNoise) {
            setError(finalize?.error || "Failed to finalize profile");
            return;
          }
          clearHomePageInfosClientCache();
          if (onComplete) {
            onComplete();
          } else {
            navigate("/home", { replace: true });
          }
          return;
        }
      } else {
        const saveNoise =
          /RESTRICTED_COUNTRIES|Undefined constant.*BusinessKey|description\(\) on null/i.test(
            String(result?.error || "")
          );
        if (saveNoise) {
          // Data already saved server-side; advance / finish instead of blocking.
          if (currentStep < STEPS.length - 1) {
            setCurrentStep((s) => s + 1);
            setFieldErrors({});
          } else {
            clearHomePageInfosClientCache();
            if (onComplete) onComplete();
            else navigate("/home", { replace: true });
          }
        } else {
          setError(result?.error || "Failed to save information");
        }
      }
    } catch (err) {
      const errNoise =
        /RESTRICTED_COUNTRIES|Undefined constant.*BusinessKey|description\(\) on null/i.test(
          String(err?.message || "")
        );
      if (errNoise) {
        if (currentStep < STEPS.length - 1) {
          setCurrentStep((s) => s + 1);
          setFieldErrors({});
        } else {
          clearHomePageInfosClientCache();
          if (onComplete) onComplete();
          else navigate("/home", { replace: true });
        }
      } else {
        setError(err.message || "An error occurred");
      }
    } finally {
      setSaving(false);
    }
  };

  const renderField = (label, icon, value, onChange, opts = {}) => {
    const { fieldKey, type = "text", placeholder, fullWidth = false, optional = false } = opts;
    const locked = fieldKey ? isLocked(fieldKey) : true;
    const hasError = fieldKey && fieldErrors[fieldKey];
    return (
      <div
        className={`profile-form-group ${fullWidth ? "profile-form-group--full" : ""}`}
        data-field-key={fieldKey}
      >
        <label className="profile-form-label">
          {label}
          {!locked && !optional && <span className="profile-required-star">*</span>}
        </label>
        <div className="profile-input-wrapper">
          <span className="profile-input-icon">
            <i className={`fa-solid ${icon}`}></i>
          </span>
          <CustomInput
            type={type}
            inputMode={type === "number" ? "numeric" : undefined}
            placeholder={placeholder || label}
            value={value}
            onChange={(e) => {
              if (!locked) {
                const nextValue =
                  type === "number" ? e.target.value.replace(/\D/g, "") : e.target.value;
                onChange(nextValue);
                if (fieldKey) clearFieldError(fieldKey);
              }
            }}
            className={`profile-input ${hasError ? "profile-input--error" : ""}`}
            disabled={locked || saving}
            readOnly={locked}
          />
        </div>
      </div>
    );
  };

  const renderDropdown = (label, options, value, onChange, opts = {}) => {
    const { fieldKey, fullWidth = false, remountKey } = opts;
    const locked = fieldKey ? isLocked(fieldKey) : true;
    const hasError = fieldKey && fieldErrors[fieldKey];
    return (
      <div
        className={`profile-form-group ${fullWidth ? "profile-form-group--full" : ""}`}
        data-field-key={fieldKey}
      >
        <label className="profile-form-label">
          {label}
          {!locked && <span className="profile-required-star">*</span>}
        </label>
        <CustomDropdown
          key={remountKey}
          options={options}
          value={value}
          onChange={(v) => {
            if (!locked) {
              onChange(v);
              if (fieldKey) clearFieldError(fieldKey);
            }
          }}
          placeholder={label}
          className={`profile-dropdown ${hasError ? "profile-input--error" : ""}`}
          disabled={locked || saving}
        />
      </div>
    );
  };

  const renderCheckboxGroup = (label, options, selectedValues, onToggle, opts = {}) => {
    const { fieldKey, fullWidth = true } = opts;
    const locked = fieldKey ? isLocked(fieldKey) : true;
    const hasError = fieldKey && fieldErrors[fieldKey];
    return (
      <div
        className={`profile-form-group ${fullWidth ? "profile-form-group--full" : ""}`}
        data-field-key={fieldKey}
      >
        <label className="profile-form-label">
          {label}
          {!locked && <span className="profile-required-star">*</span>}
        </label>
        <div className={`profile-checkbox-group ${hasError ? "profile-checkbox-group--error" : ""}`}>
          {options.map((opt) => (
            <label className="profile-checkbox-row" key={opt.value}>
              <input
                type="checkbox"
                checked={selectedValues.includes(opt.value)}
                disabled={locked || saving}
                onChange={() => onToggle(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderYesNoRadio = (label, value, onChange, opts = {}) => {
    const { fieldKey, fullWidth = true } = opts;
    const locked = fieldKey ? isLocked(fieldKey) : true;
    return (
      <div
        className={`profile-form-group ${fullWidth ? "profile-form-group--full" : ""}`}
        data-field-key={fieldKey}
      >
        <label className="profile-form-label">
          {label}
          {!locked && <span className="profile-required-star">*</span>}
        </label>
        <div className="profile-radio-group">
          <label className="radio-option">
            <input
              type="radio"
              checked={value === 0}
              disabled={locked || saving}
              onChange={() => {
                onChange(0);
                if (fieldKey) clearFieldError(fieldKey);
              }}
            />
            <span className="custom-radio" />
            <span className="radio-text">No</span>
          </label>
          <label className="radio-option">
            <input
              type="radio"
              checked={value === 1}
              disabled={locked || saving}
              onChange={() => {
                onChange(1);
                if (fieldKey) clearFieldError(fieldKey);
              }}
            />
            <span className="custom-radio" />
            <span className="radio-text">Yes</span>
          </label>
        </div>
      </div>
    );
  };

  const renderStep1 = () => (
    <div className="profile-form">
      <h3 className="profile-section-title">Account Information</h3>
      <div className="profile-form-row">
        {renderDropdown("Client Type", clientTypeOptions, clientType, setClientType, {
          fieldKey: "clientType",
        })}
        {renderDropdown("Account Type", internalTypeOptions, accountType, setAccountType, {
          fieldKey: "accountType",
        })}
      </div>
      <div className="profile-form-row">
        {renderField("Nickname", "fa-id-badge", nickName, setNickName, {
          fieldKey: "nickName",
        })}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="profile-form">
      <h3 className="profile-section-title">Personal Information</h3>
      <div className="profile-form-row">
        {renderField("First Name", "fa-id-badge", firstName, setFirstName, {
          fieldKey: "firstName",
        })}
        {renderField("Father Name", "fa-user", fatherName, setFatherName, {
          fieldKey: "fatherName",
        })}
      </div>
      <div className="profile-form-row">
        {renderField("Last Name", "fa-id-badge", lastName, setLastName, {
          fieldKey: "lastName",
        })}
        {renderField("Mother Name", "fa-user", motherName, setMotherName, {
          fieldKey: "motherName",
        })}
      </div>
      <div className="profile-form-row">
        {renderDropdown("Gender", profileGenderOptions, gender, setGender, {
          fieldKey: "gender",
        })}
        {renderField("Place of Birth", "fa-location-dot", placeOfBirth, setPlaceOfBirth, {
          fieldKey: "placeOfBirth",
        })}
      </div>
      <div className="profile-form-row">
        {renderDropdown("Marital Status", profileMaritalOptions, maritalStatus, setMaritalStatus, {
          fieldKey: "maritalStatus",
        })}
        {renderField("Birth Date", "fa-calendar-days", birthDate, setBirthDate, {
          fieldKey: "birthDate",
          type: "date",
        })}
      </div>
      {maritalStatus?.value === 1 && (
        <div className="profile-form-row">
          {renderField("Spouse Full Name", "fa-user", spouseName, setSpouseName, {
            fieldKey: "spouseName",
          })}
          {renderField("Spouse Profession", "fa-briefcase", spouseProfession, setSpouseProfession, {
            fieldKey: "spouseProfession",
          })}
        </div>
      )}
      <div className="profile-checkbox-row">
        <input
          type="checkbox"
          id="pep-checkbox"
          checked={isPep}
          onChange={(e) => {
            if (!isLocked("isPep")) setIsPep(e.target.checked);
          }}
          disabled={isLocked("isPep") || saving}
        />
        <label htmlFor="pep-checkbox">
          Are you a Politically Exposed Person (PEP) or a Relative/Close Associate of a PEP?
        </label>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="profile-form">
      <h3 className="profile-section-title">Address Information</h3>
      <div className="profile-doc-tabs profile-doc-tabs--address-mode" role="tablist" aria-label="Address information options">
        <button
          type="button"
          role="tab"
          aria-selected={addressEntryMode === "details"}
          className={`profile-doc-tab ${addressEntryMode === "details" ? "profile-doc-tab--active" : ""}`}
          onClick={() => selectAddressMode("details")}
          disabled={saving}
        >
          <i className="fa-solid fa-pen"></i>
          <span>Enter address details</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={addressEntryMode === "proof"}
          className={`profile-doc-tab ${addressEntryMode === "proof" ? "profile-doc-tab--active" : ""}`}
          onClick={() => selectAddressMode("proof")}
          disabled={saving}
        >
          <i className="fa-solid fa-file-arrow-up"></i>
          <span>Upload proof of address</span>
        </button>
      </div>
      <p className="profile-address-mode-note">
        {addressEntryMode === "proof"
          ? "Upload a proof of address. You do not need to fill in the address fields."
          : "Fill in the address fields. You do not need to upload a proof of address."}
      </p>
      {addressEntryMode === "details" && (
      <>
      <div className="profile-form-row" style={{ flexDirection: "column" }}>
        {renderField("State", "fa-location-crosshairs", state, setState, {
          fieldKey: "state",
        })}
        {renderField("City", "fa-city", city, setCity, {
          fieldKey: "city",
        })}
      </div>
      <div className="profile-form-row">
        {renderField("Zip Code", "fa-hashtag", zipCode, setZipCode, {
          fieldKey: "zipCode",
          type: "number",
        })}
        {renderField("Neighborhood", "fa-map", neighborhood, setNeighborhood, {
          fieldKey: "neighborhood",
        })}
      </div>
      <div className="profile-form-row">
        {renderField("Street", "fa-road", street, setStreet, {
          fieldKey: "street",
        })}
        {renderField("Building Name", "fa-building", buildingName, setBuildingName, {
          fieldKey: "buildingName",
        })}
      </div>
      <div className="profile-form-row">
        {renderField("Beside To", "fa-location-dot", besideTo, setBesideTo, {
          fieldKey: "besideTo",
        })}
        {renderField("Floor", "fa-layer-group", floor, setFloor, {
          fieldKey: "floor",
          type: "number",
        })}
      </div>
      <div className="profile-form-row">
        {renderField("Block", "fa-border-all", block, setBlock, {
          fieldKey: "block",
          optional: true,
        })}
        {renderField("Building Number", "fa-hashtag", buildingNumber, setBuildingNumber, {
          fieldKey: "buildingNumber",
          optional: true,
        })}
      </div>
      <div className="profile-form-row">
        {renderField("Street Number", "fa-hashtag", streetNumber, setStreetNumber, {
          fieldKey: "streetNumber",
          optional: true,
        })}
        {renderField("Landline Phone", "fa-phone", landlinePhone, setLandlinePhone, {
          fieldKey: "landlinePhone",
          type: "number",
        })}
      </div>
      <div className="profile-form-row">
        {renderField("Box Number", "fa-box", boxNumber, setBoxNumber, {
          fieldKey: "boxNumber",
          optional: true,
        })}
        {renderField("Work Address", "fa-building", workAddress, setWorkAddress, {
          fieldKey: "workAddress",
        })}
      </div>
      <div className="profile-form-row">
        {renderDropdown("Resident Status", residenceOptions, residence, setResidence, {
          fieldKey: "residence",
          fullWidth: true,
        })}
      </div>
      <div className="profile-form-row">
        <div className="profile-form-group">
          <label className="profile-form-label">
            Country
            {!isLocked("country") && <span className="profile-required-star">*</span>}
          </label>
          <CustomDropdown
            options={countryOptions}
            value={country ? { value: country, label: countryOptions.find((o) => o.value === country)?.label || country } : null}
            onChange={(v) => {
              if (!isLocked("country")) {
                setCountry(v ? v.value : "");
                clearFieldError("country");
              }
            }}
            placeholder="Country"
            searchable={true}
            className={`profile-dropdown ${fieldErrors.country ? "profile-input--error" : ""}`}
            disabled={isLocked("country") || saving}
          />
        </div>
        <div className="profile-form-group">
          <label className="profile-form-label">
            Nationality
            {!isLocked("nationality") && <span className="profile-required-star">*</span>}
          </label>
          <CustomDropdown
            options={countryOptions}
            value={nationality ? { value: nationality, label: countryOptions.find((o) => o.value === nationality)?.label || nationality } : null}
            onChange={(v) => {
              if (!isLocked("nationality")) {
                setNationality(v ? v.value : "");
                clearFieldError("nationality");
              }
            }}
            placeholder="Nationality"
            searchable={true}
            className={`profile-dropdown ${fieldErrors.nationality ? "profile-input--error" : ""}`}
            disabled={isLocked("nationality") || saving}
          />
        </div>
      </div>
      </>
      )}

      {addressEntryMode === "proof" && (
        <>
          <h3 className="profile-section-title profile-section-title--sub">Address Proof</h3>
          <div className="profile-form-row">
            {renderDropdown("Address Document Type", addressDocTypeOptions, addressDocType, setAddressDocType, {
              fieldKey: "addressDocType",
              fullWidth: true,
            })}
          </div>
          <div className="profile-form-row profile-form-row--uploads">
            {renderFileUpload("Address Document", addressFile, setAddressFile, "addressFile")}
          </div>
        </>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="profile-form">
      <h3 className="profile-section-title">Regulation Information</h3>
      <div className="profile-form-row">
        {renderField("Tax ID", "fa-receipt", taxId, setTaxId, {
          fieldKey: "taxId",
          type: "number",
        })}
        {renderDropdown("Net Worth", netWorthOptions, netWorth, setNetWorth, {
          fieldKey: "netWorth",
        })}
      </div>
      <div className="profile-form-row">
        {renderDropdown("Annual Income", annualIncomeOptions, annualIncome, setAnnualIncome, {
          fieldKey: "annualIncome",
        })}
        {renderDropdown("Annual Deposit", annualDepositOptions, annualDeposit, setAnnualDeposit, {
          fieldKey: "annualDeposit",
        })}
      </div>
      <div className="profile-form-row">
        {renderDropdown(
          "Employment Industry",
          employmentIndustryOptions,
          employmentIndustry,
          (v) => {
            setEmploymentIndustry(v);
            setEmploymentSubcategory(null);
            setProfession("");
          },
          { fieldKey: "employmentIndustry" }
        )}
        {renderDropdown(
          "Employment Status",
          employmentStatusOptions,
          employmentStatus,
          (v) => {
            setEmploymentStatus(v);
            setEmploymentSubcategory(null);
            setProfession("");
          },
          { fieldKey: "employmentStatus" }
        )}
      </div>
      <div className="profile-form-row">
        {renderDropdown(
          "Profession",
          getSubcategoriesForIndustry(employmentIndustry?.value, employmentStatus?.value),
          employmentSubcategory &&
            getSubcategoriesForIndustry(employmentIndustry?.value, employmentStatus?.value).some(
              (o) => o.value === employmentSubcategory.value
            )
            ? employmentSubcategory
            : null,
          (v) => {
            setEmploymentSubcategory(v);
            if (v && !subcategoryRequiresProfession(v.value)) {
              setProfession(v.label || getSubcategoryLabel(v.value) || "");
            } else {
              setProfession("");
            }
          },
          {
            fieldKey: "employmentSubcategory",
            remountKey: `subcat-${employmentIndustry?.value ?? "x"}-${employmentStatus?.value ?? "x"}`,
          }
        )}
        {subcategoryRequiresProfession(employmentSubcategory?.value) &&
          renderField("Specify", "fa-briefcase", profession, setProfession, {
            fieldKey: "profession",
            placeholder: "Please specify your exact occupation, job title or profession",
          })}
      </div>
      <div className="profile-form-row">
        {renderDropdown("Education Level", educationLevelOptions, educationLevel, setEducationLevel, {
          fieldKey: "educationLevel",
        })}
        {renderDropdown("Source of Wealth", sourceOfWealthOptions, sourceOfWealth, setSourceOfWealth, {
          fieldKey: "sourceOfWealth",
        })}
      </div>

      <h3 className="profile-section-title profile-section-title--sub">Trading & Funding</h3>
      <div className="profile-form-row">
        {renderDropdown("Trading Strategy", tradingStrategyOptions, tradingStrategy, (v) => {
          setTradingStrategy(v);
          if (v?.value !== 4) setTradingStrategyOther("");
        }, { fieldKey: "tradingStrategy" })}
        {renderDropdown("Monthly Deposit", monthlyDepositOptions, monthlyDeposit, setMonthlyDeposit, {
          fieldKey: "monthlyDeposit",
        })}
      </div>
      {showTradingStrategyOther && !isLocked("tradingStrategyOther") && (
        <div className="profile-form-row">
          {renderField("Specify Strategy", "fa-pen", tradingStrategyOther, setTradingStrategyOther, {
            fieldKey: "tradingStrategyOther",
            placeholder: "Please specify your trading strategy",
          })}
        </div>
      )}
      <div className="profile-form-row">
        {renderDropdown("Funding Method", fundingMethodOptions, fundingMethod, (v) => {
          setFundingMethod(v);
          if (v?.value !== 6) setFundingMethodOther("");
        }, {
          fieldKey: "fundingMethods",
        })}
      </div>
      {showFundingMethodOther && !isLocked("fundingMethodOther") && (
        <div className="profile-form-row">
          {renderField("Specify Funding Method", "fa-pen", fundingMethodOther, setFundingMethodOther, {
            fieldKey: "fundingMethodOther",
            placeholder: "Please specify your funding method",
          })}
        </div>
      )}
      <div className="profile-form-row">
        {renderYesNoRadio("Do you deposit funds in your own name?", depositsFromOwnName, setDepositsFromOwnName, {
          fieldKey: "depositsFromOwnName",
        })}
      </div>
      <div className="profile-form-row">
        {renderYesNoRadio("Are you trading for yourself?", tradingForYourself, (v) => {
          setTradingForYourself(v);
          if (v === 1) {
            setTradingOnBehalfOf(null);
            setTradingOnBehalfOther("");
          }
        }, { fieldKey: "tradingForYourself" })}
      </div>
      {showTradingOnBehalfOf && !isLocked("tradingForYourself") && (
        <>
          <div className="profile-form-row">
            <div className="profile-form-group profile-form-group--full">
              <span className="profile-field-note">
                To share mandatory document of authorisation
              </span>
            </div>
          </div>
          <div className="profile-form-row">
            {renderDropdown("Trading On Behalf Of", tradingOnBehalfOfOptions, tradingOnBehalfOf, (v) => {
              setTradingOnBehalfOf(v);
              if (v?.value !== 4) setTradingOnBehalfOther("");
            }, { fieldKey: "tradingOnBehalfOf" })}
            {showTradingOnBehalfOther &&
              renderField("Specify", "fa-pen", tradingOnBehalfOther, setTradingOnBehalfOther, {
                fieldKey: "tradingOnBehalfOther",
                placeholder: "Please specify",
              })}
          </div>
        </>
      )}

      <h3 className="profile-section-title">Trading Experience</h3>
      <div className="profile-form-row">
        {renderDropdown("Forex Experience", experienceOptions, experienceForex, setExperienceForex, {
          fieldKey: "experienceForex",
        })}
        {renderDropdown("Stocks Experience", experienceOptions, experienceStocks, setExperienceStocks, {
          fieldKey: "experienceStocks",
        })}
      </div>
      <div className="profile-form-row">
        {renderDropdown("Futures Experience", experienceOptions, experienceFutures, setExperienceFutures, {
          fieldKey: "experienceFutures",
        })}
        {renderDropdown("CFD Experience", experienceOptions, experienceCfd, setExperienceCfd, {
          fieldKey: "experienceCfd",
        })}
      </div>
    </div>
  );

  const renderFileUpload = (label, file, setFile, fieldKey, accept = "image/*") => {
    const hasError = fieldKey && fieldErrors[fieldKey];
    return (
      <div
        className={`profile-upload-area ${hasError ? "profile-upload-area--error" : ""}`}
        data-field-key={fieldKey}
      >
        <label className="profile-form-label">
          {label} <span className="profile-required-star">*</span>
        </label>
        <div className="profile-upload-box" onClick={() => document.getElementById(`file-${fieldKey}`).click()}>
          {file ? (
            <div className="profile-upload-preview">
              {file.type?.startsWith("image/") ? (
                <img src={URL.createObjectURL(file)} alt={label} className="profile-upload-thumb" />
              ) : (
                <span className="profile-upload-filename">{file.name}</span>
              )}
              <button
                type="button"
                className="profile-upload-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                }}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          ) : (
            <div className="profile-upload-placeholder">
              <i className="fa-solid fa-cloud-arrow-up"></i>
              <span>Click to upload</span>
            </div>
          )}
        </div>
        <input
          id={`file-${fieldKey}`}
          type="file"
          accept={accept}
          style={{ display: "none" }}
          onChange={(e) => {
            if (e.target.files?.[0]) {
              setFile(e.target.files[0]);
              if (fieldKey) clearFieldError(fieldKey);
            }
          }}
        />
      </div>
    );
  };

  const renderStep5 = () => (
    <div className="profile-form">
      <h3 className="profile-section-title">Documents</h3>

      <div className="profile-form-row profile-form-row--uploads">
        {renderFileUpload("Selfie Photo", selfieFile, setSelfieFile, "selfieFile")}
      </div>

      <h3 className="profile-section-title profile-section-title--sub">Identity Document</h3>
      <div className="profile-form-row">
        {renderDropdown("Document Type", identityDocTypeOptions, identityDocType, (val) => {
          setIdentityDocType(val);
          if (ONE_FACE_DOC_TYPES.includes(val?.value)) setBackIdFile(null);
          clearFieldError("identityDocType");
        }, {
          fieldKey: "identityDocType",
          fullWidth: true,
        })}
      </div>
      <div className="profile-form-row profile-form-row--uploads">
        {renderFileUpload(
          ONE_FACE_DOC_TYPES.includes(identityDocType?.value) ? "Main Page" : "Front Face",
          frontIdFile,
          setFrontIdFile,
          "frontIdFile"
        )}
        {!ONE_FACE_DOC_TYPES.includes(identityDocType?.value) &&
          renderFileUpload("Back Face", backIdFile, setBackIdFile, "backIdFile")}
      </div>

      <h3 className="profile-section-title profile-section-title--sub">Signature Video (Optional)</h3>
      <div className="profile-form-row profile-form-row--uploads">
        {renderFileUpload("Video", signatureVideoFile, setSignatureVideoFile, "signatureVideoFile", "video/*")}
      </div>
    </div>
  );

  const agreementsToSign = applicableAgreements.slice(0, requiredSignatureCount);

  const toggleAgreementCheck = (id, value) => {
    setAgreementChecks((prev) => ({ ...prev, [id]: value }));
    if (!value) {
      setAllAgreementsChecked(false);
      sigCanvasRefs.current[id]?.clear();
      setAgreementSigned((prev) => ({ ...prev, [id]: false }));
    }
  };

  const toggleAllAgreements = (checked) => {
    setAllAgreementsChecked(checked);
    const newChecks = {};
    const clearedSignatures = {};
    for (const ag of applicableAgreements) {
      const id = ag.ID ?? ag.id ?? 0;
      newChecks[id] = checked;
      if (!checked) {
        clearedSignatures[id] = false;
        sigCanvasRefs.current[id]?.clear();
      }
    }
    setAgreementChecks(newChecks);
    if (!checked) setAgreementSigned((prev) => ({ ...prev, ...clearedSignatures }));
  };

  const openAgreementDetails = (agreementKey, name) => {
    if (!agreementKey) return;
    setAgreementDetailsModal({
      open: true,
      key: agreementKey,
      name: name || "Agreement",
    });
  };

  const closeAgreementDetails = () => {
    setAgreementDetailsModal({ open: false, key: "", name: "" });
  };

  const renderStep6 = () => (
    <div className="profile-form">
      <h3 className="profile-section-title">Agreements</h3>
      {applicableAgreements.length > 0 && (
        <p className="profile-agreement-progress">
          {signedAgreementCount} out of {applicableAgreements.length} signed. Sign at least {requiredSignatureCount}. Check the rest.
        </p>
      )}

      {loadingAgreements && (
        <div className="profile-loading-message">Loading agreements...</div>
      )}

      {!loadingAgreements && agreements.length === 0 && (
        <p className="profile-no-agreements">No agreements available.</p>
      )}

      {agreements.map((ag) => {
        const id = ag.ID ?? ag.id ?? 0;
        const name = ag.AGREEMENT_NAME || ag.NAME || ag.KEY || "";
        const key = (ag.KEY || ag.NAME || "").toLowerCase();
        const agreementKey = ag.KEY || ag.NAME || ag.ID || ag.id || "";
        if (key === "ib_agreement" && profileData?.type !== 2) return null;
        const isChecked = !!agreementChecks[id];

        return (
          <div
            key={id}
            className="profile-agreement-item"
            data-field-key={`agreement-check-${id}`}
          >
            <label className="profile-agreement-checkbox">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => toggleAgreementCheck(id, e.target.checked)}
              />
              <span
                className="profile-agreement-name"
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openAgreementDetails(agreementKey, name);
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  e.preventDefault();
                  e.stopPropagation();
                  openAgreementDetails(agreementKey, name);
                }}
                style={{ cursor: "pointer", textDecoration: "underline" }}
                title="Click to view full agreement"
              >
                {name}
              </span>
            </label>
          </div>
        );
      })}

      {agreements.length > 0 && (
        <label className="profile-agreement-checkbox profile-agreement-checkbox--all">
          <input
            type="checkbox"
            checked={allAgreementsChecked}
            onChange={(e) => toggleAllAgreements(e.target.checked)}
          />
          <span className="profile-agreement-name">Accept All Agreements</span>
        </label>
      )}

      {allAgreementsChecked && agreementsToSign.map((ag, index) => {
        const id = ag.ID ?? ag.id ?? 0;
        return (
          <div
            key={`sign-${id}`}
            className="profile-signature-block"
            data-field-key={`agreement-sign-${id}`}
          >
            <label className="profile-form-label">Signature {index + 1}</label>
            <div className="profile-signature-canvas-wrapper">
              <SignatureCanvas
                ref={(ref) => { sigCanvasRefs.current[id] = ref; }}
                penColor="#141414"
                backgroundColor="#ffffff"
                clearOnResize={false}
                onEnd={() => markAgreementSigned(id)}
                canvasProps={{
                  className: "profile-signature-canvas",
                  width: 600,
                  height: 200,
                }}
              />
            </div>
            <div className="profile-signature-actions">
              <button
                type="button"
                className="profile-sig-btn profile-sig-btn--undo"
                onClick={() => {
                  undoSignatureStroke(sigCanvasRefs.current[id]);
                  markAgreementSigned(id);
                }}
              >
                <i className="fa-solid fa-rotate-left"></i> Undo
              </button>
              <button
                type="button"
                className="profile-sig-btn profile-sig-btn--clear"
                onClick={() => {
                  sigCanvasRefs.current[id]?.clear();
                  markAgreementSigned(id);
                }}
              >
                <i className="fa-solid fa-eraser"></i> Clear
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const stepRenderers = [renderStep1, renderStep2, renderStep3, renderStep4, renderStep5, renderStep6];

  return (
    <div className="profile-information-content profile-information-content--wizard">
      {error && (
        <div className="profile-error-message">
          <strong>Error:</strong> {error}
        </div>
      )}
      {loading && (
        <div className="profile-loading-message">
          Loading profile information...
        </div>
      )}

      <div className="profile-information-container">
        <div className="profile-steps-bar">
          {STEPS.map((label, i) => (
            <React.Fragment key={i}>
              <div className="profile-step-item">
                <div
                  className={`profile-step-circle ${
                    i < currentStep
                      ? "profile-step-circle--completed"
                      : i === currentStep
                      ? "profile-step-circle--active"
                      : "profile-step-circle--inactive"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`profile-step-line ${
                    i < currentStep ? "profile-step-line--active" : ""
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {!loading && stepRenderers[currentStep]()}

        {!loading && (
          <div className="profile-nav-buttons profile-nav-buttons--single">
            <button
              className={
                currentStep === STEPS.length - 1
                  ? "profile-btn-save"
                  : "profile-btn-next"
              }
              onClick={handleNext}
              disabled={saving || (currentStep === 1 && isPep)}
              type="button"
            >
              {saving ? (
                "Saving..."
              ) : currentStep === STEPS.length - 1 ? (
                <>
                  <i className="fa-solid fa-check"></i>
                  Save & Complete
                </>
              ) : (
                <>
                  Next
                  <i className="fa-solid fa-arrow-right"></i>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <AgreementDetailsModal
        isOpen={agreementDetailsModal.open}
        onClose={closeAgreementDetails}
        agreementKey={agreementDetailsModal.key}
        agreementName={agreementDetailsModal.name}
        userId={resolvedUserId}
      />
    </div>
  );
}
